import { useEffect, useState } from "react";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import LoadingDisplay from "../common/LoadingDisplay";
import { Form, InputGroup, Table } from "react-bootstrap";
import { PlusCircleFill, Search, TrashFill } from "react-bootstrap-icons";
import {
  GlossaryTextExceptionModel,
  glossaryTextExceptionApi,
} from "../api/glossaryTextExceptionApi";
import GlossaryTextExceptionModal from "../components/modals/GlossaryTextExceptionModal";
import { authUtils } from "../common/authUtils";
import { useAuth } from "react-oidc-context";

function GlossaryTextExceptionPage() {
  const auth = useAuth();
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("");
  const [glossaryTextExceptions, setGlossaryTextExceptions] = useState<
    GlossaryTextExceptionModel[]
  >([]);
  const [filteredGlossaryTextExceptions, setFilteredGlossaryTextExceptions] =
    useState<GlossaryTextExceptionModel[]>([]);
  const [modalText, setModalText] = useState<string>("");
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [newGlossaryTextException, setNewGlossaryTextException] = useState<
    GlossaryTextExceptionModel | undefined
  >(undefined);
  const [
    GlossaryTextExceptionBeingEdited,
    setGlossaryTextExceptionBeingEdited,
  ] = useState<GlossaryTextExceptionModel | undefined>(undefined);
  const [show, setShow] = useState<boolean>(false);

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    glossaryTextException?: GlossaryTextExceptionModel,
  ) => {
    setModalText(modalText);
    setIsCreate(isCreate);
    setNewGlossaryTextException(glossaryTextException);
    setGlossaryTextExceptionBeingEdited(glossaryTextException);
    setShow(true);
  };

  useEffect(() => {
    async function loadGlossaryTextExceptions() {
      setLoading(true);
      const data = await glossaryTextExceptionApi.getGlossaryTextExceptions();

      if (data.ok) {
        setError(undefined);
        setGlossaryTextExceptions(data.value);
        setFilteredGlossaryTextExceptions(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }

    loadGlossaryTextExceptions();
  }, []);

  useEffect(() => {
    filterGlossaryTextExceptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, glossaryTextExceptions]);

  const filterGlossaryTextExceptions = () => {
    var filteredGlossaryTextExceptions = [...glossaryTextExceptions];

    filteredGlossaryTextExceptions = filteredGlossaryTextExceptions.filter(
      (glossaryTextException) =>
        glossaryTextException.text
          ?.toUpperCase()
          .includes(filter.toUpperCase()),
    );

    setFilteredGlossaryTextExceptions(filteredGlossaryTextExceptions);
  };

  const onGlossaryTextExceptionChanged = (
    glossaryTextException: GlossaryTextExceptionModel,
  ) => {
    setError(undefined);
    let updatedGlossaryTextException = glossaryTextExceptions.map(
      (g: GlossaryTextExceptionModel) => {
        return g === GlossaryTextExceptionBeingEdited
          ? glossaryTextException
          : g;
      },
    );

    setGlossaryTextExceptions(updatedGlossaryTextException);
    filterGlossaryTextExceptions();
    setGlossaryTextExceptionBeingEdited(undefined);
  };

  const onNewGlossaryTextException = (
    glossaryTextException: GlossaryTextExceptionModel,
  ) => {
    setGlossaryTextExceptions([
      ...glossaryTextExceptions,
      glossaryTextException,
    ]);

    setNewGlossaryTextException(undefined);

    setTimeout(() => {
      var scrollingElement = document.querySelector("div.fixed-table-header");
      scrollingElement?.scrollTo(0, scrollingElement?.scrollHeight);
    }, 50);
  };

  const deleteGlossaryTextException = (
    glossaryTextException: GlossaryTextExceptionModel,
  ) => {
    glossaryTextExceptionApi
      .deleteGlossaryTextException(glossaryTextException.text)
      .then((response) => {
        if (response.ok) {
          setGlossaryTextExceptions(
            glossaryTextExceptions.filter((g) => g !== glossaryTextException),
          );
        } else {
          setError(response.problem);
        }
      });
  };

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Glossary Text Exceptions
          {authUtils.isAdministrator(auth) && (
            <PlusCircleFill
              className="text-primary"
              onClick={() => handleShow(true, "Add Text Exception", undefined)}
              width={32}
              height={32}
              style={{ marginLeft: "0.5rem" }}
              role="button"
            />
          )}
        </h1>
        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />

        {!loading && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  Filter
                </Form.Label>
              </span>
              <Form.Control
                size="sm"
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
              ></Form.Control>
            </InputGroup>
          </div>
        )}
        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "95%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover>
                <thead>
                  <tr>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Text
                      </Form.Label>
                    </th>
                    <th style={{ width: "1px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGlossaryTextExceptions.map(
                    (glossaryTextException, index) => (
                      <tr
                        role="button"
                        key={index}
                        onClick={() =>
                          handleShow(
                            false,
                            "Edit Text Exception",
                            glossaryTextException,
                          )
                        }
                      >
                        <td>{glossaryTextException.text}</td>
                        <td>
                          {authUtils.isAdministrator(auth) && (
                            <TrashFill
                              className="text-danger"
                              height={24}
                              width={24}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  window.confirm(
                                    "Are you sure the exception can be deleted?",
                                  )
                                ) {
                                  deleteGlossaryTextException(
                                    glossaryTextException,
                                  );
                                }
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
      {show && authUtils.isAdministrator(auth) ? (
        <GlossaryTextExceptionModal
          glossaryTextException={
            newGlossaryTextException ?? {
              text: undefined,
            }
          }
          isCreate={isCreate}
          onChange={
            isCreate
              ? onNewGlossaryTextException
              : onGlossaryTextExceptionChanged
          }
          onCancel={() => {
            setNewGlossaryTextException(undefined);
            setGlossaryTextExceptionBeingEdited(undefined);
            setShow(false);
          }}
          modalText={modalText}
        />
      ) : null}
    </>
  );
}

export default GlossaryTextExceptionPage;
